import { FC, ReactNode } from 'react';
import { useLocation } from 'wouter';
import { GenesisRole } from '../RoleEntryButtons';
import { useGamenics, GamenicsEvent } from '../../hooks/useGamenics';
import { Icon } from '../icons/Icon';
import { SiteAssets, assetLayerStyle } from '../../hooks/useSiteAssets';
import { Post } from '../../services/api';

/**
 * Districts — 三役の街の3街区（文壇サロン／トキワ荘／声劇ステージ）
 *
 * 事実性の規律（調停原則3: 損失回避・数値は事実のみ）:
 * - 街区のコミュニティ機能は準備中のため「準備中」を明示し、架空のライブ数値
 *   （「いま24人が執筆中」等のモック演出）は表示しない。
 * - 表示する数値は実データのみ: 役割ごとの「参加枠が残る起点作品」件数
 *   （role_limits / child_counts から算出）。取得失敗・0件時は静かに非表示。
 * - CTAは実在機能「その役割で起点をつくる」（GEN-01）に接続。3街区で対等。
 */

type DistrictDef = {
    id: string;
    cls: string;
    kicker: string;
    title: string;
    tagline: string;
    features: { name: string; desc: string }[];
    role: GenesisRole;
    enterLabel: string;   // 街区への入場CTA（Phase 1「開街」）
    path: string;         // 遷移先の街区ページ
    slotNoun: string;     // 「物語」「絵」「声」— 参加枠の実数表示に使う
    assetKey: keyof SiteAssets;
    reversed?: boolean;
};

// β（Phase 1「開街」）で実際に開いている機能のみ掲載（事実性①: 未実装のv2機能は載せない）
const DISTRICTS: DistrictDef[] = [
    {
        id: 'salon',
        cls: 'town-district--write',
        kicker: "WRITERS' LOUNGE",
        title: '文壇サロン',
        tagline: '語り合いが、次の一行を生む。「書いた後」ではなく「書く手前と最中」を支える円卓ラウンジ。',
        features: [
            { name: '制作ノート', desc: '想い・裏話のスレッド' },
            { name: '私の書架', desc: '影響を受けた本棚で自己紹介' },
            { name: '合評ルーム', desc: '優しい署名制インライン注釈' },
        ],
        role: 'script',
        enterLabel: '文壇サロンに入る',
        path: '/salon',
        slotNoun: '物語',
        assetKey: 'salonBg',
    },
    {
        id: 'tokiwa',
        cls: 'town-district--draw',
        kicker: "ILLUSTRATORS' ATELIER",
        title: 'トキワ荘',
        tagline: '同じ部屋で、同じキャンバスに。寝食を共にするように腕を磨く、描き手の下宿。',
        features: [
            { name: 'デッサンモード', desc: 'タイマーつきクロッキー' },
            { name: 'お題スケッチ', desc: '週替わりモチーフを一枚' },
        ],
        role: 'illustration',
        enterLabel: 'トキワ荘に入る',
        path: '/tokiwa',
        slotNoun: '絵',
        assetKey: 'tokiwaBg',
        reversed: true,
    },
    {
        id: 'stage',
        cls: 'town-district--act',
        kicker: "PERFORMERS' STAGE",
        title: '声劇ステージ',
        tagline: '台本を持ち寄り、声だけで幕を上げる。役を募り、声を持ち寄って一本の上演に。',
        features: [
            { name: '台本ライブラリ', desc: '台本を共有・閲覧' },
            { name: 'キャスティング募集', desc: '役・裏方を募り連結再生で通し聴き' },
        ],
        role: 'voice',
        enterLabel: '声劇ステージに入る',
        path: '/stage',
        slotNoun: '声',
        assetKey: 'stageBg',
    },
];

/** 役割の参加枠が残っている起点作品の件数（role_limits と child_counts の実データから算出） */
const countOpenPosts = (posts: Post[], role: GenesisRole): number => {
    const key = role === 'script' ? 'text' : role === 'illustration' ? 'image' : 'audio';
    return posts.filter(p => {
        if (!p.roleLimits || !p.childCounts) return false;
        return (p.childCounts[key] ?? 0) < (p.roleLimits[key] ?? 0);
    }).length;
};

// ─── ミニ画面モック（構想中の画面イメージ。装飾であり操作対象ではない） ───

const SalonScreen: FC = () => (
    <div className="town-screen" role="img" aria-label="開発中の画面イメージ: 台本と合評注釈が並ぶ合評ルーム">
        <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-ink bg-cream text-ink font-bold">
            <span className="flex gap-1" aria-hidden>
                {[0, 1, 2].map(i => <i key={i} className="w-2 h-2 rounded-full border border-ink bg-white" />)}
            </span>
            合評ルーム — 「雨宿りの駅」第2稿
        </div>
        <div className="p-3 grid grid-cols-[1.4fr_1fr] gap-2">
            <div>
                <div className="px-2 py-1.5 rounded-pop-sm text-ink-2">終電を逃した夜、無人駅の</div>
                <div className="px-2 py-1.5 rounded-pop-sm text-ink bg-role-script-soft border-l-[3px] border-role-script">
                    スピーカーから聞こえてきたのは、十年前に消えたはずの声だった。
                </div>
                <div className="px-2 py-1.5 rounded-pop-sm text-ink-2">ぼくはベンチから立ち上がれずにいた。</div>
            </div>
            <div>
                <div className="px-2 py-1.5 mb-2 rounded-pop-sm bg-cream border border-ink/20 text-ink-2">
                    <b className="text-role-script-text font-bold">tsumugi:</b> この転換、鳥肌でした
                </div>
                <div className="px-2 py-1.5 rounded-pop-sm bg-cream border border-ink/20 text-ink-2">
                    <b className="text-role-script-text font-bold">kanade:</b> 声にした時の「間」が想像できる一文
                </div>
            </div>
        </div>
    </div>
);

const TokiwaScreen: FC = () => (
    <div className="town-screen" role="img" aria-label="開発中の画面イメージ: トキワ荘の部屋マップと共有キャンバス">
        <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-ink bg-cream text-ink font-bold">
            <span className="flex gap-1" aria-hidden>
                {[0, 1, 2].map(i => <i key={i} className="w-2 h-2 rounded-full border border-ink bg-white" />)}
            </span>
            荘マップ — 廊下から部屋を選ぶ
        </div>
        <div className="p-3 grid grid-cols-2 gap-2">
            <div className="p-3 rounded-pop-sm border-2 border-role-illustration bg-role-illustration-soft">
                <b className="block text-ink font-bold">合作部屋</b>
                <span className="text-ink-2">みんなで1枚を描く</span>
            </div>
            <div className="p-3 rounded-pop-sm border border-ink/20 bg-cream">
                <b className="block text-ink font-bold">デッサン部屋</b>
                <span className="text-ink-2">初心者歓迎・1分ポーズ</span>
            </div>
            <div
                className="col-span-2 h-[72px] rounded-pop-sm border border-ink/20 relative"
                style={{
                    background:
                        'radial-gradient(80px 40px at 30% 60%, rgba(245,158,11,0.35), transparent 70%),' +
                        'radial-gradient(60px 40px at 70% 30%, rgba(139,92,246,0.3), transparent 70%), #fff',
                }}
            >
                <span className="absolute bottom-1.5 left-2 text-ink-2 font-bold">共有キャンバス</span>
            </div>
        </div>
    </div>
);

const StageScreen: FC = () => (
    <div className="town-screen" role="img" aria-label="開発中の画面イメージ: 上演中のステージと演出コンソール">
        <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-ink bg-cream text-ink font-bold">
            <span className="flex gap-1" aria-hidden>
                {[0, 1, 2].map(i => <i key={i} className="w-2 h-2 rounded-full border border-ink bg-white" />)}
            </span>
            本番ステージ — 上演イメージ
        </div>
        <div className="p-3">
            <div className="rounded-pop-sm border border-ink/20 overflow-hidden">
                <div className="px-3 py-4 text-center text-white" style={{ background: 'linear-gradient(rgba(36,28,46,0.82), rgba(36,28,46,0.94)), #5b21b6' }}>
                    <div className="text-white/70">（屋上。夜風。遠くに街の灯り）</div>
                    <div className="font-bold text-sm">「ねえ、あと何個数えたら朝が来ると思う？」</div>
                </div>
                <div className="flex gap-2 px-3 py-2 border-t border-ink/20 bg-cream">
                    <span className="flex-1 flex items-center gap-1.5 text-ink-2 font-bold">
                        ミナ役 kanade
                        <span className="inline-flex items-end gap-[2px] h-3.5" aria-hidden>
                            {[40, 90, 60, 100, 50].map((h, i) => (
                                <i key={i} className="w-[3px] rounded-sm bg-role-voice" style={{ height: `${h}%` }} />
                            ))}
                        </span>
                    </span>
                    <span className="flex-1 flex items-center gap-1.5 text-ink-2 font-bold">ぼく役 ren</span>
                </div>
                <div className="flex gap-2 px-3 py-2 border-t border-ink/20 bg-white">
                    <span className="px-2.5 py-1 rounded-full bg-role-voice-soft text-role-voice-text font-bold">照明: 夜</span>
                    <span className="px-2.5 py-1 rounded-full border border-ink/20 text-ink-2 font-bold">背景: 屋上</span>
                    <span className="px-2.5 py-1 rounded-full border border-ink/20 text-ink-2 font-bold">BGM: 夜風</span>
                </div>
            </div>
        </div>
    </div>
);

const SCREENS: Record<string, ReactNode> = {
    salon: <SalonScreen />,
    tokiwa: <TokiwaScreen />,
    stage: <StageScreen />,
};

// ─── 街区セクション本体 ───

export const Districts: FC<{
    assets: SiteAssets;
    posts: Post[];
    postsLoaded: boolean; // false = 読み込み中/失敗 → 件数は表示しない（偽の0を出さない）
    onCreateGenesis: (role: GenesisRole) => void;
}> = ({ assets, posts, postsLoaded, onCreateGenesis }) => {
    const { playSound } = useGamenics();
    const [, setLocation] = useLocation();

    return (
        <div className="w-full max-w-[1120px] mx-auto px-6">
            {DISTRICTS.map(d => {
                const openCount = postsLoaded ? countOpenPosts(posts, d.role) : 0;
                const info = (
                    <div>
                        <span className="town-kicker">{d.kicker}</span>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <h2 className="font-display font-black text-ink text-[clamp(24px,3vw,31px)]">{d.title}</h2>
                            {/* Phase 1「開街」: 準備中バッジは撤去（3街区同時／マスター§6・T3） */}
                        </div>
                        <p className="mt-2 text-ink-2 text-sm">{d.tagline}</p>
                        <p className="mt-4 text-[12px] font-bold text-ink-2">この街区でできること:</p>
                        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 list-none p-0">
                            {d.features.map(f => (
                                <li key={f.name} className="flex gap-2 text-sm text-ink-2">
                                    <span className="town-feature-dot" aria-hidden />
                                    <span><strong className="text-ink font-bold">{f.name}</strong>&nbsp;{f.desc}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="flex items-center gap-4 mt-6 flex-wrap">
                            {/* 街区への入場（Phase 1「開街」・3街区で対等）。起点づくりは街区ページ側で PIPE-01 に引き継ぐ */}
                            <button
                                className="town-enter"
                                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(d.path); }}
                            >
                                {d.enterLabel} <Icon name="arrow-right" size={15} />
                            </button>
                            {/* この役割の起点をつくる（GEN-01・従来導線をサブに保持） */}
                            <button
                                className="text-[13px] font-bold text-ink-2 underline hover:text-ink min-h-[44px]"
                                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); onCreateGenesis(d.role); }}
                            >
                                {d.slotNoun}の起点をつくる
                            </button>
                            {/* 実データのみ表示（0件・未取得時は非表示） */}
                            {openCount > 0 && (
                                <span className="text-[12px] font-bold text-ink-2 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full" style={{ background: 'var(--rb)' }} aria-hidden />
                                    {d.slotNoun}の参加枠が残る起点 <span className="tabular-nums font-display">{openCount}</span> 件
                                </span>
                            )}
                        </div>
                    </div>
                );
                const screen = <div>{SCREENS[d.id]}</div>;

                return (
                    <section
                        key={d.id}
                        id={d.id}
                        className={`town-district ${d.cls} mt-8 scroll-mt-20`}
                        style={assetLayerStyle(assets[d.assetKey])}
                        aria-label={`街区: ${d.title}`}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 items-center">
                            {d.reversed ? (<><div className="order-1 md:order-2">{info}</div><div className="order-2 md:order-1">{screen}</div></>) : (<>{info}{screen}</>)}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};
